/**
 * De buurtkiezer: welke gebieden een zoekopdracht afloopt.
 *
 * De lijst is Funda's eigen gebiedenlijst, zodra een ronde die heeft
 * binnengebracht; tot die tijd staan er alleen de slugs in die al in een
 * zoekopdracht voorkomen. Wie een buurt kent die er niet in staat plakt de slug
 * uit een Funda-URL (`amsterdam/de-pijp`) - de zoekterm wordt dan zelf een keuze.
 *
 * Zelfde vorm als de buurtkeuze in de filterbalk: een zoekveld, gekozen regels
 * bovenaan, en een lijst die schuift.
 */
import { type Gebied, gebiedSlugSchema } from '@mamimap/shared';
import { X } from 'lucide-react';
import { useMemo, useState } from 'react';

type BuurtKiezerProps = {
  opties: readonly Gebied[];
  gekozen: readonly string[];
  onWijzig: (gebieden: string[]) => void;
  label: (slug: string) => string;
};

export function BuurtKiezer({ opties, gekozen, onWijzig, label }: BuurtKiezerProps) {
  const [zoek, zetZoek] = useState('');
  const term = zoek.trim().toLowerCase();

  function wissel(slug: string) {
    onWijzig(
      gekozen.includes(slug) ? gekozen.filter((bestaand) => bestaand !== slug) : [...gekozen, slug],
    );
  }

  const gevonden = useMemo(() => {
    const treffers = opties.filter(
      (gebied) =>
        term === '' ||
        gekozen.includes(gebied.slug) ||
        gebied.naam.toLowerCase().includes(term) ||
        gebied.slug.includes(term),
    );
    return [
      ...treffers.filter((gebied) => gekozen.includes(gebied.slug)),
      ...treffers.filter((gebied) => !gekozen.includes(gebied.slug)),
    ];
  }, [opties, gekozen, term]);

  /* Een zoekterm die zelf een slug is en nog niet in de lijst staat, is te
     kiezen als nieuw gebied. Zo kom je ook aan een buurt die Funda's lijst nog
     niet noemde. */
  const alsSlug = gebiedSlugSchema.safeParse(term);
  const nieuwSlug =
    alsSlug.success && !opties.some((gebied) => gebied.slug === alsSlug.data) ? alsSlug.data : null;

  return (
    <div className="flex flex-col gap-1.5">
      {gekozen.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {gekozen.map((slug) => (
            <span key={slug} className="chip" data-actief="true" title={slug}>
              {label(slug)}
              <button
                type="button"
                onClick={() => wissel(slug)}
                aria-label={`${label(slug)} weghalen`}
                className="text-accent-deep/70 hover:text-accent-deep"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="text-[12px] text-muted">Nog geen gebied gekozen.</p>
      )}

      <input
        className="veld"
        type="search"
        aria-label="Gebied zoeken"
        placeholder="Zoek een buurt, of plak een slug zoals amsterdam/de-pijp"
        value={zoek}
        onChange={(event) => zetZoek(event.target.value)}
      />

      <div className="max-h-56 overflow-y-auto border border-line bg-surface-strong">
        {nieuwSlug ? (
          <button
            type="button"
            onClick={() => {
              wissel(nieuwSlug);
              zetZoek('');
            }}
            className="flex w-full items-center gap-2 border-b border-line px-2 py-[4px] text-left text-[12px] text-accent-deep hover:bg-paper"
          >
            <span>Toevoegen</span>
            <span className="cijfers truncate">{nieuwSlug}</span>
          </button>
        ) : null}

        {gevonden.map((gebied) => {
          const aan = gekozen.includes(gebied.slug);
          return (
            <button
              type="button"
              key={gebied.slug}
              onClick={() => wissel(gebied.slug)}
              aria-pressed={aan}
              className={`flex w-full items-center gap-2 px-2 py-[3px] text-left text-[12px] ${
                aan ? 'bg-accent-soft text-accent-deep' : 'text-ink-soft hover:bg-paper'
              }`}
            >
              <span className="truncate">{gebied.naam}</span>
              <span className="truncate text-[11.5px] text-muted">{gebied.slug}</span>
              {gebied.soort ? (
                <span className="ml-auto shrink-0 text-[11px] text-faint">
                  {soortLabel(gebied.soort)}
                </span>
              ) : null}
            </button>
          );
        })}

        {gevonden.length === 0 && !nieuwSlug ? (
          <p className="px-2 py-2 text-[12px] text-muted">
            {opties.length === 0
              ? 'De gebiedenlijst van Funda is er nog niet; plak een slug uit een Funda-URL.'
              : 'Geen gebied met deze naam.'}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** Funda's woord voor de soort, vertaald voor het oog; onbekend blijft zoals het is. */
function soortLabel(soort: string): string {
  switch (soort) {
    case 'city':
    case 'municipality':
      return 'plaats';
    case 'district':
      return 'wijk';
    case 'neighbourhood':
    case 'neighborhood':
      return 'buurt';
    case 'province':
      return 'provincie';
    default:
      return soort;
  }
}
